import Stack from '@mui/material/Stack';
import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { usePartnersReadMe } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import LoadErrorState from '@/components/LoadErrorState';
import AccountStatusSkeleton from '@/domain/account-status/components/AccountStatusSkeleton';
import StatusPending from '@/domain/account-status/components/StatusPending';
import StatusRejected from '@/domain/account-status/components/StatusRejected';
import StatusSuspended from '@/domain/account-status/components/StatusSuspended';
import { getPartnerGate } from '@/domain/account-status/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';

interface Props {
  /** The page an account in review or approved can use. */
  children: (partner: Partner) => ReactNode;
  /** Shows the "in review" notice above the page (P05, on the events list). */
  showPendingNotice?: boolean;
}

/**
 * Gate in front of every partner page (P05–P07): the onboarding before an account exists, a
 * blocking page for a rejected or suspended account, the page itself otherwise.
 */
const AccountStatus = ({ children, showPendingNotice = false }: Props) => {
  const t = useTranslate('domain.account-status.AccountStatus');
  const { data: account, isLoading, isError, refetch } = usePartnersReadMe();

  if (isLoading) return <AccountStatusSkeleton />;
  if (isError) return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;

  const gate = getPartnerGate(account);
  const partner = account?.partner;

  if (gate === 'onboarding' || !partner) return <Navigate to={ScreenRoutes.ONBOARDING} replace />;
  if (gate === 'rejected') return <StatusRejected partner={partner} />;
  if (gate === 'suspended') return <StatusSuspended partner={partner} />;
  if (gate === 'pending_review' && showPendingNotice) {
    return (
      <Stack gap={3}>
        <StatusPending />
        {children(partner)}
      </Stack>
    );
  }
  return children(partner);
};

export default AccountStatus;
